import { latLngToMercatorMeters, latLngToPixel, type LatLng } from './GeoMath';
import { applyMatrix3, solveHomography, type Point2 } from './Homography';
import {
  createFrame,
  frameBounds,
  frameIsRectangular,
  type MapFrame,
} from './MapFrame';
import { TILE_SIZE, tileMercatorBounds } from './MapView';

/**
 * Where the aerial image comes from.
 *
 * ## Why this is a list rather than "Google"
 *
 * The request was for a map view so nobody has to screenshot one by hand, and Google is the obvious
 * place to point it. Two things argue against wiring it in as *the* source:
 *
 * - **Terms.** Google Maps Platform's terms restrict extracting Maps Content for use outside the
 *   Google services, and this feature's whole purpose is to send the image to a third-party vision
 *   model and then store it in a project file. That is a question for whoever runs the account, not
 *   something to answer silently in code — so Google is offered, with the question stated on it, and
 *   it is not the default.
 * - **Keys.** A default that cannot draw anything until someone has created a billing account is a
 *   default that makes the feature look broken.
 *
 * So the shipped default is public-domain USGS imagery, which needs no key and no permission, and
 * the keyed providers sit alongside it. Everything a provider needs is declared as data — endpoint
 * shape, resolution ceiling, attribution, licence note — which also means adding a state
 * orthoimagery WMS later is a table entry rather than a code path.
 *
 * ## Two endpoint shapes, and no others
 *
 * Every static imagery API in practice is one of:
 *
 * - **centre/zoom** — `center=lat,lng&zoom=z&size=WxH&scale=s` (Google, Mapbox)
 * - **bbox** — a projected EPSG:3857 rectangle and a pixel size (ArcGIS `exportImage`, WMS)
 *
 * Both produce the same north-up Web Mercator rectangle, which is why {@link MapFrame} can be
 * provider-agnostic and why swapping providers does not move a single metre of the site.
 */

export type ImageryEndpoint = 'center-zoom' | 'bbox';

export interface ImageryProvider {
  id: string;
  name: string;
  /** One line for the picker: what it is good for, and where it works. */
  summary: string;
  endpoint: ImageryEndpoint;
  /** Whether a key or token must be supplied before anything will draw. */
  needsKey: boolean;
  /** Label for the key field, so it says "access token" where that is what it is. */
  keyLabel?: string;
  /** Where to get one, shown next to the field. */
  keyHint?: string;
  /** Required credit line. Displayed under the map and stored with the frame. */
  attribution: string;
  /**
   * What the licence actually permits, in plain words. Shown in the picker, because "can I send this
   * image to a model and save it in a file" is a question the user must answer before capturing,
   * not after.
   */
  licenceNote: string;
  /** Rough ground resolution ceiling, in metres per pixel. Below this the imagery just blurs. */
  bestResolutionMeters: number;
  /**
   * Largest image edge the endpoint will actually return, in pixels — after any `@2x` or `scale=2`,
   * so it is directly comparable to {@link MapFrame.pixels}.
   */
  maxPixels: number;
  /**
   * The finest ground resolution the endpoint will serve, in metres per pixel.
   *
   * Separate from {@link bestResolutionMeters}, which says where the imagery stops carrying new
   * detail. This says where the *server* stops answering — and the two are far apart. Esri's
   * World_Imagery export happily oversamples its 0.3 m mosaic down to about 0.05 m/px and returns
   * HTTP 500 with an `Error: bytes` page below that, which reads to a user as "the map is broken".
   * A 30 m frame at 1280 px asks for 0.023 m/px, so the default frame size was enough to trigger it.
   *
   * `frameImageUrl` clamps the requested pixel count against this. The bbox is unchanged, so the
   * capture still covers exactly the ground the frame claims — it just comes back smaller.
   */
  minMetersPerPixel: number;
  /** True where coverage is not global, so the picker can say so. */
  coverage: 'global' | 'united-states';
  /**
   * How the *browsing* layer gets its pixels, which is a separate question from how a capture does.
   *
   * - `xyz` — a real slippy tile service. Cheapest and smoothest.
   * - `bbox-tiles` — no tile cache, but an image server that will answer one tile-shaped bbox at a
   *   time. Identical requests between gestures, so the browser cache carries most of them.
   * - `none` — nothing we may legitimately serve into a pan-and-zoom map. Browsing falls back to
   *   {@link ImageryProvider.browseFallbackId}; capture still comes from this provider.
   */
  browse: 'xyz' | 'bbox-tiles' | 'none';
  /** Deepest integer zoom the browse layer will ask for. Past it the service returns nothing. */
  maxTileZoom: number;
  /** Whose tiles stand in when {@link ImageryProvider.browse} is `none`. */
  browseFallbackId?: string;
}

export const IMAGERY_PROVIDERS: readonly ImageryProvider[] = [
  {
    id: 'usgs-naip',
    name: 'USGS NAIP aerial',
    summary: 'Public-domain US aerial photography. No key, no account, ~0.6 m per pixel.',
    endpoint: 'bbox',
    needsKey: false,
    attribution: 'USGS National Agriculture Imagery Program',
    licenceNote:
      'Public domain — a work of the US federal government. Free to capture, to send to a model, ' +
      'and to keep in a project file. Refreshed every 2–3 years, so it is rarely the newest imagery ' +
      'of a site.',
    bestResolutionMeters: 0.6,
    maxPixels: 4096,
    // Comfortably coarser than the 0.6 m source: a NAIP pixel has no detail below this, and the
    // ImageServer gets very slow when asked to resample far past it.
    minMetersPerPixel: 0.15,
    coverage: 'united-states',
    // An ImageServer with no tile cache, so the browse layer asks it for one tile-shaped bbox at a
    // time rather than a fresh viewport-shaped image on every gesture.
    browse: 'bbox-tiles',
    maxTileZoom: 19,
  },
  {
    id: 'esri-world-imagery',
    name: 'Esri World Imagery',
    summary: 'Global satellite and aerial mosaic. No key. Attribution required.',
    endpoint: 'bbox',
    needsKey: false,
    attribution: 'Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    licenceNote:
      'Free to view with the credit line kept visible. Esri’s terms for this service do not clearly ' +
      'cover sending captures to a third-party model — check before using it on client work.',
    bestResolutionMeters: 0.3,
    maxPixels: 4096,
    // Measured against the live service: 0.055 m/px returns an image, 0.044 m/px returns a 500.
    minMetersPerPixel: 0.06,
    coverage: 'global',
    browse: 'xyz',
    maxTileZoom: 19,
  },
  {
    id: 'mapbox-satellite',
    name: 'Mapbox Satellite',
    summary: 'Global, high resolution, generous free tier. Needs an access token.',
    endpoint: 'center-zoom',
    needsKey: true,
    keyLabel: 'Mapbox access token',
    keyHint: 'account.mapbox.com → Tokens. A default public token works.',
    attribution: '© Mapbox © Maxar',
    licenceNote:
      'Static Images API. Mapbox permits caching and downstream use far more explicitly than most; ' +
      'still check your plan’s terms before sending captures to a model.',
    bestResolutionMeters: 0.3,
    // 1280 × 1280, requested as 640@2x — the Static Images API's ceiling.
    maxPixels: 1280,
    minMetersPerPixel: 0.05,
    coverage: 'global',
    browse: 'xyz',
    maxTileZoom: 20,
  },
  {
    id: 'google-satellite',
    name: 'Google Maps Satellite',
    summary: 'The imagery most people mean. Needs a Maps Platform key and billing.',
    endpoint: 'center-zoom',
    needsKey: true,
    keyLabel: 'Google Maps API key',
    keyHint: 'console.cloud.google.com → Maps Platform → Static Maps API, with billing enabled.',
    attribution: 'Map data © Google',
    licenceNote:
      'Read this one before you use it. Google Maps Platform terms restrict extracting Maps Content ' +
      'for use outside Google’s services — sending a capture to a third-party vision model and ' +
      'storing it in a project file is very likely outside them. Fine for trying the feature on your ' +
      'own site; ask before it touches client work.',
    bestResolutionMeters: 0.15,
    // 640 × 640 at `scale=2` is 1280 px back — the standard tier's ceiling.
    maxPixels: 1280,
    minMetersPerPixel: 0.05,
    coverage: 'global',
    // Google's tiles may only be drawn by Google's own map surfaces, so there is no honest way to
    // serve them into this pan-and-zoom layer. You browse on Esri and capture from Google, and the
    // UI says so — the two are the same Web Mercator rectangle, so the georeference is identical and
    // only the imagery's vintage differs.
    browse: 'none',
    browseFallbackId: 'esri-world-imagery',
    maxTileZoom: 20,
  },
];

export function imageryProvider(id: string): ImageryProvider {
  return IMAGERY_PROVIDERS.find((provider) => provider.id === id) ?? (IMAGERY_PROVIDERS[0] as ImageryProvider);
}

/** The provider a fresh session starts on: global enough to be useful, and needs nothing. */
export const DEFAULT_IMAGERY_PROVIDER = 'esri-world-imagery';

/**
 * Which provider actually draws the browsing map for a chosen capture provider.
 *
 * Usually itself. Google is the exception, and the substitution is surfaced in the UI rather than
 * done quietly — "the picture you framed on is not the picture you captured" is exactly the kind of
 * thing that must not be a silent behaviour.
 */
export function browseProvider(provider: ImageryProvider): ImageryProvider {
  if (provider.browse !== 'none') return provider;
  return imageryProvider(provider.browseFallbackId ?? DEFAULT_IMAGERY_PROVIDER);
}

/**
 * The URL for one browse tile.
 *
 * Separate from {@link frameImageUrl} on purpose. That one has to be *exact* — it is the capture,
 * and its pixels become the site. This one only has to be good enough to aim with, so it is allowed
 * to be a 256 px JPEG from a cache, which is what makes panning cost nothing.
 *
 * Returns `null` when the provider cannot be browsed at all; callers resolve that with
 * {@link browseProvider} before getting here.
 */
export function tileUrl(
  provider: ImageryProvider,
  z: number,
  x: number,
  y: number,
  key?: string,
): string | null {
  if (provider.browse === 'bbox-tiles') {
    const bounds = tileMercatorBounds(z, x, y);
    const bbox = `${bounds.minX},${bounds.minY},${bounds.maxX},${bounds.maxY}`;
    if (provider.id === 'usgs-naip') {
      return (
        'https://imagery.nationalmap.gov/arcgis/rest/services/USGSNAIPPlus/ImageServer/exportImage' +
        `?bbox=${bbox}&bboxSR=3857&imageSR=3857&size=${TILE_SIZE},${TILE_SIZE}&format=jpgpng&f=image`
      );
    }
    return (
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export' +
      `?bbox=${bbox}&bboxSR=3857&imageSR=3857&size=${TILE_SIZE},${TILE_SIZE}` +
      '&format=jpg&transparent=false&f=image'
    );
  }

  if (provider.browse !== 'xyz') return null;

  if (provider.id === 'mapbox-satellite') {
    if (!key) return null;
    return (
      `https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}@2x.jpg90` +
      `?access_token=${encodeURIComponent(key)}`
    );
  }

  // Esri's cached tile service. Note the row/column order: `/tile/{z}/{y}/{x}`, not `{x}/{y}`.
  return (
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile' +
    `/${z}/${y}/${x}`
  );
}

/**
 * The URL that renders a frame.
 *
 * Deterministic and pure, so the same frame always produces the same URL — which is what lets the
 * view use it as an `<img src>` and the capture use it as a `fetch`, with no risk of the two showing
 * different ground.
 */
/**
 * How many pixels to actually ask this provider for.
 *
 * Three ceilings, and the third is the one that was missing: the frame's own request, the endpoint's
 * hard maximum, and the finest resolution the service will serve. A small frame at the full 1280 px
 * asks for sub-centimetre ground and gets an HTTP 500 — so the count comes down and the *bbox stays
 * the same*, which means the capture still covers exactly the ground the frame claims and only the
 * detail it was never going to get is given up.
 */
export function capturePixels(frame: MapFrame, provider: ImageryProvider): number {
  const requested = Math.min(frame.pixels, provider.maxPixels);
  if (!(provider.minMetersPerPixel > 0)) return requested;
  const finest = Math.floor(frame.widthMeters / provider.minMetersPerPixel);
  // No minimum size to fight it with. A small frame of coarse imagery genuinely has few pixels in
  // it — 10 m of a 0.3 m mosaic is thirty-odd real ones — and a floor here would put the request
  // back over the line the ceiling exists to keep it under.
  return Math.max(1, Math.min(requested, finest));
}

export function frameImageUrl(frame: MapFrame, provider: ImageryProvider, key?: string): string {
  // `frame.pixels` is the image that comes back; `scale` is only how the request phrases it. So a
  // bbox endpoint is asked for the whole number and a centre-zoom endpoint for the divided one, and
  // both return the same picture of the same ground.
  const pixels = capturePixels(frame, provider);
  const scale = frame.scale;

  if (provider.endpoint === 'bbox') {
    const bounds = frameBounds(frame);
    const min = latLngToMercatorMeters({ lat: bounds.south, lng: bounds.west });
    const max = latLngToMercatorMeters({ lat: bounds.north, lng: bounds.east });
    const bbox = `${min.x},${min.y},${max.x},${max.y}`;
    const size = `${pixels},${pixels}`;

    if (provider.id === 'usgs-naip') {
      return (
        'https://imagery.nationalmap.gov/arcgis/rest/services/USGSNAIPPlus/ImageServer/exportImage' +
        `?bbox=${bbox}&bboxSR=3857&imageSR=3857&size=${size}&format=png&f=image`
      );
    }
    return (
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export' +
      `?bbox=${bbox}&bboxSR=3857&imageSR=3857&size=${size}&format=png&transparent=false&f=image`
    );
  }

  const requested = Math.max(1, Math.round(pixels / Math.max(1, scale)));
  const center = `${frame.center.lat.toFixed(7)},${frame.center.lng.toFixed(7)}`;

  if (provider.id === 'mapbox-satellite') {
    const at = scale > 1 ? '@2x' : '';
    return (
      'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/' +
      `${frame.center.lng.toFixed(7)},${frame.center.lat.toFixed(7)},${frame.zoom},0/` +
      `${requested}x${requested}${at}?access_token=${encodeURIComponent(key ?? '')}&attribution=false&logo=false`
    );
  }

  return (
    'https://maps.googleapis.com/maps/api/staticmap' +
    `?center=${center}&zoom=${frame.zoom}&size=${requested}x${requested}&scale=${scale}` +
    `&maptype=satellite&format=png&key=${encodeURIComponent(key ?? '')}`
  );
}

export class ImageryError extends Error {
  constructor(
    message: string,
    /** True when the failure is the browser refusing to hand over pixels, not the server refusing to serve them. */
    readonly isCors = false,
  ) {
    super(message);
    this.name = 'ImageryError';
  }
}

/**
 * Turn a frame into an actual image file.
 *
 * ## Two problems, in order
 *
 * **Getting the pixels.** Getting *bytes* out of a cross-origin image is a different problem from
 * *displaying* one, and which works depends on headers this app does not control:
 *
 * 1. `fetch` → `Blob`. Clean, and works wherever the endpoint sends permissive CORS headers, which
 *    the ArcGIS services and Mapbox do.
 * 2. A `crossOrigin="anonymous"` image drawn to a canvas. Reaches endpoints that allow the image but
 *    not the fetch.
 *
 * When both fail the frame is still worth keeping: the georeference is already known, so the caller
 * can tell the user to capture that rectangle by hand and the site is still built at the right
 * scale. That is why the error distinguishes a CORS refusal from a server error.
 *
 * **Getting the right pixels.** Every static imagery endpoint serves an axis-aligned, north-up
 * rectangle — there is no request that returns a turned or four-cornered one. So a shaped frame is
 * fetched as its *bounding box*, oversampled, and then {@link rectifyFrame} warps the quad onto the
 * output square on a canvas here. A north-up square frame skips all of that and is fetched directly,
 * which keeps the common case exact and free of a resampling generation.
 */
export async function captureFrameImage(
  frame: MapFrame,
  provider: ImageryProvider,
  key?: string,
  signal?: AbortSignal,
): Promise<File> {
  if (provider.needsKey && !key) {
    throw new ImageryError(`${provider.name} needs a ${provider.keyLabel ?? 'key'} before it can draw.`);
  }
  const name = frameFileName(frame);

  // An untouched frame *is* its own bounding box, so warping it would cost a resample to reproduce
  // the picture already fetched.
  if (frame.headingDegrees === 0 && frameIsRectangular(frame)) {
    const blob = await fetchImageBlob(frameImageUrl(frame, provider, key), provider, signal);
    return new File([blob], name, { type: blob.type || 'image/png' });
  }

  const source = enclosingFrame(frame);
  const blob = await fetchImageBlob(frameImageUrl(source, provider, key), provider, signal);
  const rectified = await rectifyFrame(blob, source, frame);
  return new File([rectified], name, { type: 'image/png' });
}

/**
 * The north-up frame that encloses a shaped one.
 *
 * Oversampled: a 45°-turned square needs a bounding box √2 wider, and taking that at the same pixel
 * count would hand the warp an image with 40 % less detail than the site deserves. The zoom is
 * chosen for the *enclosure*, so the output square ends up at roughly the resolution it would have
 * had unturned.
 */
export function enclosingFrame(frame: MapFrame): MapFrame {
  const bounds = frameBounds(frame);
  const corners = frame.corners.map((point) => latLngToPixel(point, frame.zoom));
  const spanX = Math.max(...corners.map((p) => p.x)) - Math.min(...corners.map((p) => p.x));
  const spanY = Math.max(...corners.map((p) => p.y)) - Math.min(...corners.map((p) => p.y));
  const growth = Math.max(spanX, spanY) / Math.max(1, frame.pixels / Math.max(1, frame.scale));

  return createFrame({
    center: { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 },
    sizeMeters: Math.max(frame.widthMeters, frame.lengthMeters) * Math.max(1, growth) * 1.02,
    providerId: frame.providerId,
    pixels: frame.pixels,
    scale: frame.scale,
  });
}

/**
 * Warp the quad out of a north-up capture and onto the output square.
 *
 * Done per output pixel with an inverse map and bilinear sampling. That is a million-odd iterations
 * of plain arithmetic for a 1280² frame — a few hundred milliseconds, once, on an action the user
 * already knows is a fetch. A WebGL pass would be faster and would drag a context, a shader and a
 * fallback path behind it for a saving nobody would notice.
 *
 * Bilinear rather than nearest because the whole point of a turned frame is edges that no longer run
 * along pixel rows, and nearest sampling makes every one of them a staircase.
 */
export async function rectifyFrame(source: Blob, sourceFrame: MapFrame, target: MapFrame): Promise<Blob> {
  const bitmap = await decodeImage(source);
  const size = target.pixels;

  const input = document.createElement('canvas');
  input.width = bitmap.width;
  input.height = bitmap.height;
  const inputContext = input.getContext('2d', { willReadFrequently: true });
  if (!inputContext) throw new ImageryError('This browser would not give up a 2D canvas.');
  inputContext.drawImage(bitmap, 0, 0);
  const src = inputContext.getImageData(0, 0, bitmap.width, bitmap.height);

  // Where each of the target's corners falls in the source image, in its pixels.
  const sourceCorners = target.corners.map((corner) =>
    latLngToSourcePixel(corner, sourceFrame, bitmap.width, bitmap.height),
  ) as unknown as [Point2, Point2, Point2, Point2];

  // Output pixel -> source pixel, solved once for the whole image.
  const inverse = solveHomography(
    [
      { x: 0, y: 0 },
      { x: size, y: 0 },
      { x: size, y: size },
      { x: 0, y: size },
    ],
    sourceCorners,
  );
  if (!inverse) throw new ImageryError('That frame shape could not be squared up. Try a less extreme drag.');

  const output = document.createElement('canvas');
  output.width = size;
  output.height = size;
  const outputContext = output.getContext('2d');
  if (!outputContext) throw new ImageryError('This browser would not give up a 2D canvas.');
  const destination = outputContext.createImageData(size, size);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const at = applyMatrix3(inverse, { x: x + 0.5, y: y + 0.5 });
      const index = (y * size + x) * 4;
      if (!at) continue;
      sampleBilinear(src, at.x - 0.5, at.y - 0.5, destination.data, index);
    }
  }

  outputContext.putImageData(destination, 0, 0);
  return canvasToBlob(output);
}

/** Where a coordinate lands in a north-up capture, in that image's pixels. */
function latLngToSourcePixel(point: LatLng, frame: MapFrame, width: number, height: number): Point2 {
  const bounds = frameBounds(frame);
  const northWest = latLngToPixel({ lat: bounds.north, lng: bounds.west }, frame.zoom);
  const southEast = latLngToPixel({ lat: bounds.south, lng: bounds.east }, frame.zoom);
  const here = latLngToPixel(point, frame.zoom);
  return {
    x: ((here.x - northWest.x) / Math.max(1e-9, southEast.x - northWest.x)) * width,
    y: ((here.y - northWest.y) / Math.max(1e-9, southEast.y - northWest.y)) * height,
  };
}

/** Bilinear sample, writing RGBA into `out` at `index`. Outside the source stays transparent. */
function sampleBilinear(
  src: ImageData,
  x: number,
  y: number,
  out: Uint8ClampedArray,
  index: number,
): void {
  if (x < 0 || y < 0 || x > src.width - 1 || y > src.height - 1) return;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(src.width - 1, x0 + 1);
  const y1 = Math.min(src.height - 1, y0 + 1);
  const fx = x - x0;
  const fy = y - y0;

  const a = (y0 * src.width + x0) * 4;
  const b = (y0 * src.width + x1) * 4;
  const c = (y1 * src.width + x0) * 4;
  const d = (y1 * src.width + x1) * 4;

  for (let channel = 0; channel < 4; channel += 1) {
    const top = (src.data[a + channel] as number) * (1 - fx) + (src.data[b + channel] as number) * fx;
    const bottom = (src.data[c + channel] as number) * (1 - fx) + (src.data[d + channel] as number) * fx;
    out[index + channel] = top * (1 - fy) + bottom * fy;
  }
}

/** Fetch, with the canvas fallback for endpoints that allow the image but not the fetch. */
async function fetchImageBlob(
  url: string,
  provider: ImageryProvider,
  signal?: AbortSignal,
): Promise<Blob> {
  try {
    const response = await fetch(url, signal ? { signal } : {});
    if (!response.ok) {
      throw new ImageryError(`${provider.name} refused the request (HTTP ${response.status}).`);
    }
    const blob = await response.blob();
    if (blob.size === 0) throw new ImageryError(`${provider.name} returned an empty image.`);
    return blob;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    if (error instanceof ImageryError && !error.isCors) throw error;
    return canvasCapture(url, 'frame.png', provider);
  }
}

/** `createImageBitmap` where it exists, an `<img>` where it does not. */
async function decodeImage(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  const url = URL.createObjectURL(blob);
  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new ImageryError('The rectified frame could not be encoded.'));
    }, 'image/png');
  });
}

/** The fallback: let the browser load the image, then read it back off a canvas. */
async function canvasCapture(url: string, name: string, provider: ImageryProvider): Promise<Blob> {
  const image = await loadImage(url);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new ImageryError('This browser would not give up a 2D canvas.');
  context.drawImage(image, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new ImageryError(`${provider.name} would not release the image (${name}).`, true));
          return;
        }
        resolve(blob);
      }, 'image/png');
    } catch {
      // A tainted canvas throws on read: the pixels are on screen but the page may not have them.
      reject(
        new ImageryError(
          `${provider.name} allows this image to be displayed but not exported from the browser. ` +
            'Capture the framed rectangle by hand and upload it — the scale is already known, so ' +
            'the site will still be built at the right size.',
          true,
        ),
      );
    }
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new ImageryError('The imagery could not be loaded. Check the key and the connection.'));
    image.src = url;
  });
}

/** A file name that records where and when the ground came from, since a capture is evidence. */
export function frameFileName(frame: MapFrame): string {
  const lat = frame.center.lat.toFixed(5);
  const lng = frame.center.lng.toFixed(5);
  return `site-${lat}_${lng}-z${frame.zoom}-${Math.round(frame.widthMeters)}m.png`;
}
